import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { selectUser, selectToken } from "../auth/authSelectors";
import {
  FaReceipt,
  FaCheckCircle,
  FaTimesCircle,
  FaUsers,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import { Modal, Button } from "react-bootstrap";
import { getTransactions } from "../api/transactionService";
import "../pages/styles/transactions.css";

const FONT = {
  title: "18px",
  subtitle: "15px",
  text: "13px",
  small: "12px",
};

export default function Transactions() {
  const user = useSelector(selectUser);
  const token = useSelector(selectToken);

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const credentials = useMemo(
    () => ({ userId: user?.id, token }),
    [user?.id, token]
  );

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  // Load transactions
  useEffect(() => {
    const { userId, token } = credentials;
    if (!userId || !token) {
      setError("Authentication error");
      setLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      try {
        const res = await getTransactions(token, userId);
        if (mounted) setOrders(res.status === "success" ? res.orders : []);
      } catch (err) {
        console.error("getTransactions error:", err);
        if (mounted) setError("Unable to load transactions.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => (mounted = false);
  }, [credentials]);

  const openModal = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  return (
    <div className="container py-3" style={{ minHeight: "100vh", background: "#f4f5f7" }}>
      <h4 className="fw-bold mb-3" style={{ fontSize: FONT.title }}>
        <FaReceipt className="me-2" />
        Your Orders
      </h4>

      {loading && (
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status" />
        </div>
      )}

      {error && <div className="alert alert-danger text-center small">{error}</div>}

      {!loading && orders.length === 0 && (
        <div className="alert alert-warning text-center small">No orders found.</div>
      )}

      {!loading && orders.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle transactions-table">
            <thead className="table-light">
              <tr>
                <th>Tournament</th>
                <th>Order ID</th>
                <th className="text-end">Amount</th>
                <th>Date</th>
                <th>Status</th>
                <th>Participants</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {orders.map((o, i) => (
                <tr key={i} className="transaction-row">
                  <td data-label="Tournament" className="fw-semibold">
                    {o.tournament?.tournament_name || "—"}
                  </td>

                  <td data-label="Order ID" className="text-muted small">
                    {o.razorpay_order_id}
                  </td>

                  <td data-label="Amount" className="text-end">
                    ₹{o.amount}
                  </td>

                  <td data-label="Order Date" className="small">
                    {formatDate(o.order_date)}
                  </td>

                  <td data-label="Status">
                    {o.payment_status === "paid" ? (
                      <span className="badge bg-success status-badge">
                        <FaCheckCircle className="me-1" /> PAID
                      </span>
                    ) : (
                      <span className="badge bg-danger status-badge">
                        <FaTimesCircle className="me-1" /> FAILED
                      </span>
                    )}
                  </td>

                  <td data-label="Participants">{o.participants?.length ?? 0}</td>

                  <td>
                    <button
                      className="btn btn-outline-primary btn-sm view-btn"
                      onClick={() => openModal(o)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* MODAL */}
      {selectedOrder && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="md">
          <Modal.Header
            closeButton
            style={{
              background: "#0A66ED",
              color: "white",
              borderBottom: "none",
              padding: "14px 18px",
            }}
          >
            <Modal.Title style={{ fontWeight: 600 }}>
              <FaUsers className="me-2" />
              Order Summary
            </Modal.Title>
          </Modal.Header>

          <Modal.Body style={{ background: "#f8f9fa", padding: "18px" }}>
            {/* ORDER INFO */}
            <div
              className="p-3 mb-3 rounded"
              style={{
                background: "white",
                border: "1px solid #e8e9eb",
                borderRadius: 10,
              }}
            >
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div>
                  <div className="fw-bold">{selectedOrder.tournament?.tournament_name}</div>
                  <div className="small text-muted">
                    <FaMapMarkerAlt className="me-1" />
                    {selectedOrder.tournament?.venue || "—"}
                  </div>
                  <div className="small text-muted mt-1">
                    <FaCalendarAlt className="me-1" />
                    {formatDate(selectedOrder.tournament?.event_date)}
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <div className="small text-muted">Order Date</div>
                  <div className="fw-semibold">{formatDate(selectedOrder.order_date)}</div>
                </div>
              </div>

              <hr />

              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Order ID</span>
                <strong>{selectedOrder.razorpay_order_id}</strong>
              </div>

              <div className="d-flex justify-content-between small mb-1">
                <span className="text-muted">Amount</span>
                <strong>₹{selectedOrder.amount}</strong>
              </div>

              <div className="d-flex justify-content-between small">
                <span className="text-muted">Payment Status</span>
                {selectedOrder.payment_status === "paid" ? (
                  <span className="badge bg-success">Successful</span>
                ) : (
                  <span className="badge bg-danger">Failed</span>
                )}
              </div>
            </div>

            {/* PARTICIPANTS */}
            <h6 className="fw-semibold mb-2">Participants ({selectedOrder.participants?.length ?? 0})</h6>

            <div className="row g-2">
              {selectedOrder.participants?.map((p, idx) => (
                <div key={idx} className="col-12">
                  <div className="p-2 bg-white border rounded participant-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <div className="fw-semibold">{p.player_name}</div>
                        <div className="small text-muted">Category: {p.category}</div>
                        <div className="small text-muted">DOB: {p.dob}</div>
                      </div>
                      <div className="text-end small text-muted">₹{ (selectedOrder.amount / (selectedOrder.participants?.length || 1)).toFixed(0) }</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Modal.Body>

          <Modal.Footer style={{ borderTop: "none", padding: "12px 18px" }}>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
}
